/* ------------------------------------------------------------------
  File: Tooltip.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable tooltip component for the Accend Holdings website.
//   - Supports contextual helper text on hover and keyboard focus.
//   - Supports placement variants, visual variants, disabled state,
//     accessible labeling, and dark-surface compatibility.
//   - Suitable for icon button hints, form help, dashboard metrics,
//     disabled action notes, and short UI guidance.

/**
 * Tooltip
 *
 * A reusable tooltip component for short contextual explanations.
 *
 * @param {React.ReactNode} children
 * The trigger element that displays the tooltip on hover or focus.
 *
 * @param {React.ReactNode} content
 * The tooltip content.
 *
 * @param {"top" | "right" | "bottom" | "left"} [placement="top"]
 * Controls where the tooltip appears relative to the trigger.
 *
 * @param {"default" | "inverse"} [variant="inverse"]
 * Controls the tooltip visual style.
 *
 * @param {boolean} [disabled=false]
 * Disables the tooltip.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import { useId } from "react";
import "./Tooltip.css";

const Tooltip = ({
  children,
  content,
  placement = "top",
  variant = "inverse",
  disabled = false,
  className = "",
  ...props
}) => {
  const tooltipId = useId();

  const tooltipClasses = [
    "acc-tooltip",
    `acc-tooltip--${placement}`,
    `acc-tooltip--${variant}`,
    disabled ? "acc-tooltip--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (!content || disabled) {
    return children;
  }

  return (
    <span className={tooltipClasses} aria-describedby={tooltipId} {...props}>
      {children}

      <span id={tooltipId} role="tooltip" className="acc-tooltip__content">
        {content}
        <span className="acc-tooltip__arrow" aria-hidden="true" />
      </span>
    </span>
  );
};

export default Tooltip;